/**
 * ReservacionesTable — DataTable reutilizable para listar reservaciones.
 * - Search global con debounce
 * - Click en fila → abre SlideOver de detalle
 * - Columnas adaptativas por contexto (la sub-página decide qué mostrar)
 * - Estado vacío con icono + mensaje
 */
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Paper, Chip,
  useMediaQuery, useTheme, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DataTable from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import EstadoChip from './EstadoChip';
import SaldoDestinoChip from './SaldoDestinoChip';
import VoucherSubsanarChip from './VoucherSubsanarChip';
import EvidenciaModal from '../EvidenciaModal';
import {
  formatFechaCorta, formatHora, formatPrecio, fmtPax, norm, puedeBorrarReserva,
} from './format';
import { formatNombre } from '../../../utils/formatNombre';

const ESTADO_COLOR = {
  pendiente: '#f59e0b', confirmada: '#3b82f6', hold: '#a855f7',
  completada: '#10b981', cancelada: '#ef4444', no_show: '#7f1d1d', suspendida: '#eab308',
};

export default function ReservacionesTable({
  rows, loading, onRowClick, emptyTitle, emptyMessage,
  showSaldo = false, showAgencia = true, showFecha = true,
  defaultSortBy = 'salida_fecha', defaultSortDir = 'asc',
  // Cuando el padre pasa onDelete se muestra la columna "Acciones" con el
  // botón de borrar. El botón se desactiva si la salida ya pasó el cutoff
  // configurado en `configuracion_turismo.hora_cutoff_reservas_dia`.
  onDelete = null,
  horaCutoff = '09:00',
  // Cuando `true`, se oculta el campo de búsqueda local. Útil cuando la
  // tabla se usa dentro de un grupo (ej. bloques mensuales del histórico)
  // donde repetir el buscador por grupo es ruido visual.
  hideSearch = false,
}) {
  const [search, setSearch] = useState('');
  // Modal exclusivo de evidencia — se abre al hacer clic en la 📷 de la fila
  // (sin abrir la reserva completa). Se maneja aquí para que todas las páginas
  // que usan la tabla lo hereden sin cambios.
  const [evidenciaReserva, setEvidenciaReserva] = useState(null);
  const onOpenEvidencia = (r) => setEvidenciaReserva(r);
  // En desktop pero por debajo de xl (< 1536) la tabla está apretada: a
  // 1366×768 las columnas Código y Pax no caben sin scroll horizontal porque
  // el menú lateral de NARA come ~240px. Por debajo de xl ocultamos Código
  // (embebido en Cliente) y Pax (chip en Paquete) y reducimos paddings.
  // Mismo patrón que RecepcionTablaDensa del muelle.
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const compact = isMd && !isXl;

  const filtered = useMemo(() => {
    const q = norm(search.trim());
    if (!q) return rows;
    return rows.filter(r => (
      norm(r.codigo_reserva).includes(q) ||
      norm(r.nombre_cliente).includes(q) ||
      norm(r.numero_whatsapp).includes(q) ||
      norm(r.correo).includes(q) ||
      norm(r.agencia_nombre).includes(q) ||
      norm(r.paquete_nombre).includes(q)
    ));
  }, [rows, search]);

  const columns = [];

  // Código — oculto en compact; el operador lo ve embebido bajo el nombre.
  if (!compact) {
    columns.push({
      id: 'codigo', label: 'Código', sortable: true, sortValue: r => r.codigo_reserva || '',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: ESTADO_COLOR[r.estado] || '#cbd5e1' }} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
            {r.codigo_reserva}
          </Typography>
        </Box>
      ),
    });
  }

  // Cliente — en compact lleva una mini-banda de color a la izquierda + el
  // código abreviado debajo del nombre, recuperando la info de la columna oculta.
  columns.push({
    id: 'cliente', label: 'Cliente', sortable: true, sortValue: r => r.nombre_cliente || '',
    render: (r) => {
      // Cortesía: total de cortesías > 0 o forma_pago marcada como cortesia
      const esCortesia = (Number(r.cortesias) || 0) > 0 || r.forma_pago_agencia === 'cortesia';
      return (
        <Box sx={{ minWidth: 140, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {compact && (
            <Box sx={{ width: 3, height: 26, borderRadius: 1, bgcolor: ESTADO_COLOR[r.estado] || '#cbd5e1', flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }} noWrap>
                {formatNombre(r.nombre_cliente) || '—'}
              </Typography>
              {esCortesia && (
                <Chip size="small" label="🎁 Cortesía"
                  sx={{
                    bgcolor: '#fef3c7', color: '#92400e',
                    fontWeight: 700, fontSize: 10, height: 18,
                    flexShrink: 0, '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Box>
            {compact ? (
              <Typography sx={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }} noWrap>
                {r.codigo_reserva}{r.numero_whatsapp ? ` · ${r.numero_whatsapp}` : ''}
              </Typography>
            ) : r.numero_whatsapp ? (
              <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
                📱 {r.numero_whatsapp}
              </Typography>
            ) : null}
          </Box>
        </Box>
      );
    },
  });

  // Fecha + hora (Hoy/Próximas/Por cobrar/Histórico)
  if (showFecha) {
    columns.push({
      id: 'fecha', label: 'Salida', sortable: true, sortValue: r => r.salida_fecha || '',
      render: (r) => (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {formatFechaCorta(r.salida_fecha)}
          </Typography>
          {r.salida_hora && (
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>{formatHora(r.salida_hora)}</Typography>
          )}
        </Box>
      ),
    });
  }

  // Paquete — en compact incluye el badge de pax al lado del nombre.
  // Bajo el nombre del paquete mostramos la(s) lancha(s) asignadas, para que
  // el coordinador vea de un vistazo en qué bote viaja el pasajero.
  columns.push({
    id: 'paquete', label: 'Paquete / Tour', sortable: true, sortValue: r => r.paquete_nombre || '',
    render: (r) => {
      const lanchas = (r.salida_embarcaciones || []).filter(Boolean);
      const lanchaAlquiler = r.embarcacion_alquiler;
      const lanchaInfo = lanchas.length > 0
        ? lanchas.join(' · ')
        : (lanchaAlquiler || null);
      return (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: '#475569', flex: 1, minWidth: 0 }} noWrap>
              {r.paquete_nombre || r.alquiler_nombre || '—'}
            </Typography>
            {compact && (
              <Chip size="small" label={`${r.total_pasajeros || 0} pax`}
                sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontWeight: 700, fontSize: 10, height: 18, flexShrink: 0 }} />
            )}
          </Box>
          {lanchaInfo && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.25 }} noWrap>
              🚤 {lanchaInfo}
            </Typography>
          )}
        </Box>
      );
    },
  });

  // Pasajeros — oculta en compact; va embebido como chip en Paquete arriba.
  if (!compact) {
    columns.push({
      id: 'pax', label: 'Pax', align: 'center',
      sortable: true, sortValue: r => r.total_pasajeros || 0,
      render: (r) => (
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{r.total_pasajeros || 0}</Typography>
          <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{fmtPax(r)}</Typography>
        </Box>
      ),
    });
  }

  // Agencia
  if (showAgencia) {
    columns.push({
      id: 'agencia', label: 'Agencia / Canal',
      render: (r) => r.agencia_nombre ? (
        <Chip size="small" label={r.agencia_nombre}
          sx={{ bgcolor: '#fdf2f8', color: '#be185d', fontWeight: 600, fontSize: 11, height: 20 }} />
      ) : (
        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{r.canal_reserva || 'directo'}</Typography>
      ),
    });
  }

  // Precio o Saldo (según contexto)
  if (showSaldo) {
    columns.push({
      id: 'saldo', label: 'Saldo', align: 'right',
      sortable: true, sortValue: r => Number(r.saldo_pendiente || 0),
      render: (r) => (
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#dc2626' }}>
          {formatPrecio(r.saldo_pendiente || 0, r.moneda)}
        </Typography>
      ),
    });
  } else {
    columns.push({
      id: 'precio', label: 'Neto', align: 'right',
      // Neto Entretur = precio_total − comision_valor (bruta pactada).
      // El descuento_comision_agencia NO afecta el neto: es un descuento que
      // la agencia le regala al cliente saliendo de su propia comisión.
      sortable: true,
      sortValue: r => Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0),
      render: (r) => {
        const neto = Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0);
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488' }}>
            {formatPrecio(neto, r.moneda)}
          </Typography>
        );
      },
    });
    // Comisión por pagar: lo que Entretur le debe a la agencia después del pasadía.
    //   = comision_valor − descuento_comision_agencia − comision_anticipada
    // NO aplica en CxC agencia (esas se saldan contra el cupo, no hay comisión
    // pendiente para Entretur — la agencia asume el neto).
    // Solo se muestra el valor si es > 0. Reservas sin agencia o sin comisión
    // pendiente muestran "—".
    columns.push({
      id: 'com_pagar', label: 'Com. por pagar', align: 'right',
      sortable: true,
      sortValue: r => {
        if (!r.agencia_id) return 0;
        if ((r.saldo_a_cargo_de || '') === 'agencia') return 0;
        return Math.max(
          Number(r.comision_valor || 0)
          - Number(r.descuento_comision_agencia || 0)
          - Number(r.comision_anticipada || 0),
          0
        );
      },
      render: (r) => {
        if (!r.agencia_id) return <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>—</Typography>;
        if ((r.saldo_a_cargo_de || '') === 'agencia')
          return <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>—</Typography>;
        const pendiente = Math.max(
          Number(r.comision_valor || 0)
          - Number(r.descuento_comision_agencia || 0)
          - Number(r.comision_anticipada || 0),
          0
        );
        if (pendiente <= 0)
          return <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>—</Typography>;
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#be185d' }}>
            {formatPrecio(pendiente, r.moneda)}
          </Typography>
        );
      },
    });
  }

  // Estado + destino del saldo (dos chips en la misma celda)
  columns.push({
    id: 'estado', label: 'Estado',
    render: (r) => (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
        <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                          saldoACargoDe={r.saldo_a_cargo_de}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                          formaPagoAgencia={r.forma_pago_agencia} />
        <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
      </div>
    ),
  });

  // Checkin — badge compacto (📷 verde si tiene foto, gris si aún no).
  // Si el padre pasó `onOpenEvidencia`, el clic en la 📷 abre un modal
  // exclusivo (sin abrir toda la reserva). Sin ese prop, sigue el flujo
  // clásico (click en la fila → detalle → tab Evidencia).
  columns.push({
    id: 'checkin', label: 'Checkin', align: 'center',
    sortable: true,
    sortValue: r => (r.checkin_evidencia?.con_foto || 0),
    render: (r) => {
      const ev = r.checkin_evidencia || { total: 0, con_foto: 0, titular_con_foto: false };
      const con = ev.con_foto || 0;
      const clickable = !!onOpenEvidencia && con > 0;
      const stop = (e) => e.stopPropagation();
      const onCameraClick = clickable
        ? (e) => { e.stopPropagation(); onOpenEvidencia(r); }
        : undefined;

      if (con === 0) {
        return (
          <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: '#cbd5e1' }} titleAccess="Sin evidencia de checkin" />
        );
      }
      const color = ev.titular_con_foto ? '#10b981' : '#f59e0b';
      const label = ev.titular_con_foto
        ? (con > 1 ? `Titular + ${con - 1} acompañante(s)` : 'Titular')
        : 'Solo acompañantes';
      const IconEl = (
        <PhotoCameraIcon sx={{
          fontSize: 18, color,
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable ? { transform: 'scale(1.15)', transition: 'transform 0.1s' } : undefined,
        }} titleAccess={label} onClick={onCameraClick} />
      );
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}
             onClick={clickable ? stop : undefined}>
          {clickable ? <Tooltip title="Ver evidencia">{IconEl}</Tooltip> : IconEl}
          {con > 1 && (
            <Typography sx={{ fontSize: 10, fontWeight: 700, color }}>{con}</Typography>
          )}
        </Box>
      );
    },
  });

  // Observación IA — chip resumen del estado del OCR (rápido de escanear)
  // + tooltip con el detalle completo (nombre_capturado / motivo omisión / etc.).
  columns.push({
    id: 'observacion_ia', label: 'Observación IA', align: 'center',
    sortable: true,
    sortValue: r => (r.checkin_evidencia?.estado_ocr || 'zz'),
    render: (r) => {
      const ev = r.checkin_evidencia || {};
      const estado = ev.estado_ocr || (ev.con_foto ? 'sin_ocr' : 'sin_foto');
      const obs = ev.observacion_ia || '';

      const config = {
        ok:          { bg: '#dcfce7', color: '#166534', label: '✓ Titular OK' },
        no_coincide: { bg: '#fef3c7', color: '#a16207', label: '⚠ No coincide' },
        no_leible:   { bg: '#fee2e2', color: '#991b1b', label: '✗ No legible' },
        omitido:     { bg: '#fef3c7', color: '#a16207', label: '! Omitido' },
        sin_ocr:     { bg: '#e0f2fe', color: '#0369a1', label: 'Sin OCR' },
        sin_foto:    { bg: '#f1f5f9', color: '#94a3b8', label: '—' },
      };
      const c = config[estado] || config.sin_foto;
      const chip = (
        <Chip label={c.label} size="small"
          sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10, height: 20 }} />
      );
      return obs
        ? <Tooltip title={obs} arrow>{chip}</Tooltip>
        : chip;
    },
  });

  // Columna "Acciones" (borrar) — solo si el padre pasó `onDelete`.
  // El botón se desactiva cuando ya pasó el cutoff de la salida. El backend
  // re-valida por si el frontend está desfasado.
  if (onDelete) {
    columns.push({
      id: 'acciones', label: 'Acciones', align: 'center', sortable: false,
      render: (r) => {
        const { ok, motivo } = puedeBorrarReserva(r.salida_fecha, horaCutoff, r.estado_embarque);
        const tooltip = ok
          ? 'Borrar reserva'
          : motivo || 'Borrado no permitido';
        return (
          <Tooltip title={tooltip} arrow>
            {/* span wrapper para que Tooltip funcione cuando el botón está disabled */}
            <span onClick={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                disabled={!ok}
                onClick={(e) => {
                  e.stopPropagation();  // no dispara onRowClick
                  if (ok) onDelete(r);
                }}
                sx={{
                  color: ok ? '#dc2626' : '#cbd5e1',
                  '&:hover': ok ? { bgcolor: '#fef2f2', color: '#991b1b' } : {},
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    });
  }

  return (
    <Box>
      {!hideSearch && (
        <Box sx={{ mb: 1.5 }}>
          <TextField
            fullWidth size="small" placeholder="Buscar por código, cliente, teléfono, agencia, paquete…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                </InputAdornment>
              ),
              sx: { bgcolor: '#fff', borderRadius: 2, fontSize: 13 },
            }}
          />
        </Box>
      )}

      {!loading && filtered.length === 0 ? (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)' }}>
          <EmptyState
            icon={<InboxIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
            title={emptyTitle || 'Sin reservas'}
            message={emptyMessage || (search ? 'No hay coincidencias con tu búsqueda.' : 'Aún no hay reservas en esta categoría.')}
          />
        </Paper>
      ) : (
        // Wrapper con scroll horizontal explícito + paddings reducidos en
        // pantallas medianas (laptop 1366×768 con sidebar de NARA): permite
        // que toda la tabla quepa o, si no cabe, que el scroll sea claro y
        // funcional en táctil. El compact mode adicional achica las celdas.
        <Box sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'visible',
          // Indicadores sombra "hay más contenido a los lados" (solo desktop)
          background: `
            linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 8px) left center / 20px 100% no-repeat,
            linear-gradient(90deg, rgba(255,255,255,0) calc(100% - 8px), rgba(255,255,255,1) 100%) right center / 20px 100% no-repeat,
            linear-gradient(90deg, rgba(0,0,0,0.06), rgba(0,0,0,0)) left center / 8px 100% no-repeat,
            linear-gradient(90deg, rgba(0,0,0,0), rgba(0,0,0,0.06)) right center / 8px 100% no-repeat
          `,
          backgroundAttachment: 'local, local, scroll, scroll',
          // En compact: paddings horizontales reducidos en cada celda para que
          // más columnas quepan sin scroll (objetivo: 1366×768 con sidebar).
          ...(compact && {
            '& .MuiTableCell-root': { px: 0.75, py: 0.75 },
            '& .MuiTableCell-head': { fontSize: 10 },
          }),
          // Scrollbar discreto pero visible cuando aparece
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-track': { bgcolor: '#f1f5f9' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#94a3b8' },
        }}>
          <DataTable
            columns={columns}
            rows={filtered}
            loading={loading}
            rowKey={r => r.id}
            defaultSortBy={defaultSortBy}
            defaultSortDir={defaultSortDir}
            onRowClick={onRowClick}
          />
        </Box>
      )}

      {/* Modal exclusivo de evidencia — abierto al clic en la 📷 de una fila */}
      {evidenciaReserva && (
        <EvidenciaModal
          reservaId={evidenciaReserva.id}
          codigoReserva={evidenciaReserva.codigo_reserva}
          nombreCliente={evidenciaReserva.nombre_cliente}
          onClose={() => setEvidenciaReserva(null)}
        />
      )}
    </Box>
  );
}
